import { motion } from "framer-motion";

// Real tool/language icons via simple-icons (loaded as inline SVG path data).
// No emojis, no cartoon icons. If a brand icon isn't available, fall back to text.

interface Tool { name: string; slug: string; color: string; }

const TOOLS: Tool[] = [
  { name: "TypeScript", slug: "typescript", color: "#3178c6" },
  { name: "Python",     slug: "python",     color: "#3776ab" },
  { name: "Go",         slug: "go",         color: "#00add8" },
  { name: "Rust",       slug: "rust",       color: "#dea584" },
  { name: "React",      slug: "react",      color: "#5acaff" },
  { name: "Node.js",    slug: "nodedotjs",  color: "#5fa04e" },
  { name: "Bun",        slug: "bun",        color: "#fbf0df" },
  { name: "Vite",       slug: "vite",       color: "#646cff" },
  { name: "Tailwind",   slug: "tailwindcss",color: "#38bdf8" },
  { name: "Docker",     slug: "docker",     color: "#2496ed" },
  { name: "Kubernetes", slug: "kubernetes", color: "#326ce5" },
  { name: "PostgreSQL", slug: "postgresql", color: "#4169e1" },
  { name: "Neo4j",      slug: "neo4j",      color: "#018bff" },
  { name: "Supabase",   slug: "supabase",   color: "#3fcf8e" },
  { name: "Cloudflare", slug: "cloudflare", color: "#f38020" },
  { name: "Microsoft Azure", slug: "microsoftazure", color: "#0078d4" },
  { name: "OpenAI",     slug: "openai",     color: "#ffffff" },
  { name: "Anthropic",  slug: "anthropic",  color: "#d4a373" },
  { name: "GitHub",     slug: "github",     color: "#ffffff" },
  { name: "GitLab",     slug: "gitlab",     color: "#fc6d26" },
  { name: "n8n",        slug: "n8n",        color: "#ea4b71" },
  { name: "Atlassian",  slug: "atlassian",  color: "#0052cc" },
  { name: "Snowflake",  slug: "snowflake",  color: "#29b5e8" },
  { name: "Tableau",    slug: "tableau",    color: "#e97627" },
  { name: "Power BI",   slug: "powerbi",    color: "#f2c811" },
  { name: "PyTorch",    slug: "pytorch",    color: "#ee4c2c" },
  { name: "Hugging Face", slug: "huggingface", color: "#fffd5c" },
  { name: "DuckDB",     slug: "duckdb",     color: "#fff100" },
  { name: "Redis",      slug: "redis",      color: "#ff4438" },
  { name: "MongoDB",    slug: "mongodb",    color: "#47a248" },
];

function ToolPill({ tool }: { tool: Tool }) {
  const url = `https://cdn.simpleicons.org/${tool.slug}/${tool.color.replace("#", "")}`;
  return (
    <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.12] transition-all whitespace-nowrap">
      <img
        src={url}
        alt=""
        width={18}
        height={18}
        className="opacity-90"
        loading="lazy"
        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
      />
      <span className="text-[13px] font-medium text-slate-300">{tool.name}</span>
    </div>
  );
}

export default function ToolsMarquee() {
  // Duplicate the list so the loop is seamless under transform: -50%.
  const doubled = [...TOOLS, ...TOOLS];
  return (
    <div className="relative overflow-hidden py-3 border-y border-white/[0.05] bg-white/[0.01]">
      <div className="absolute left-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-r from-bg-950 to-transparent pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-l from-bg-950 to-transparent pointer-events-none" />
      <motion.div
        className="flex gap-3 w-max"
        animate={{ x: ["0%", "-50%"] }}
        transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
      >
        {doubled.map((t, i) => (<ToolPill key={`${t.slug}-${i}`} tool={t} />))}
      </motion.div>
    </div>
  );
}
